import React from "react";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExlyInput from "repoV2/features/Common/modules/Form/modules/ExlyInput/ExlyInput";
import ExlySearchableDropdownV2 from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdownV2/ExlySearchableDrowpdownV2";
import { indian_states_and_uts_dropdown_options } from "repoV2/features/Common/modules/Location/constants/indianStatesUtsData";
import checkoutAccordianStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import checkoutCommonStyles from "repoV2/features/CheckoutPages/CheckoutCommonStyles.module.scss";
import { classNames } from "repoV2/utils/common/render/classNames";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import CustomQuestionFormGroup from "repoV2/features/CustomQuestions/modules/CustomQuestionsFormGroup/CustomQuestionFormGroup";
import { custom_questions_helper_props } from "repoV2/features/CheckoutPages/constants/CheckoutPagesCustomQuestions";
import {
    getUserFormFieldName,
    useUserDetailsFormGroup,
    validators,
} from "../../UserDetailsFormGroup.utils";
import { useUserDetailsFormContext } from "../UserDetailsFormContextProvider";
import { checkout_form } from "../../../../../CheckoutForm/CheckoutForm.constants";
import CountryCodeAdornment from "../CountryCodeAdornment/CountryCodeAdornment";
import InternationalDetailsFormGroup from "../InternationalDetailsFormGroup/InternationalDetailsFormGroup";
import styles from "../../UserDetailsFormGroup.module.scss";

const {
    user_details: { field_names, placeholders },
} = checkout_form;

const billingStatesOptions = indian_states_and_uts_dropdown_options;

const UserDetailsFormBase = () => {
    const {
        emailPlaceholder,
        phoneNumberValidator,
        emailValidator,
        isUserEmailDisabled,
        setUserDetailsFormData,
        showAddressInsideUserDetails,
        showInternationalInputFields,
        showPaymentMethodInputField = false,
        bookingQuestions,
        setFileUploaderFileValue,
        clearFileUploaderFileValue,
        generateBookingQuestionFieldName,
        generateUploadKey,
        setIsCustomQuestionFileUploading,
        showGstBillingStateField,
        setBillingStateFormData,
        billingStateValidator,
        isBillingStateDisabled,
        isBillingStateMandatory,
        currencyName,
    } = useUserDetailsFormGroup();
    const { userId } = useUserDetailsFormContext();

    return (
        <div className={checkoutAccordianStyles.BasicForm}>
            <ExlyInput
                name={getUserFormFieldName(userId, field_names.name)}
                classes={{
                    helperText: checkoutCommonStyles.helperText,
                }}
                registerOptions={{
                    validate: validators[field_names.name],
                }}
                overrideInputProps={{
                    placeholder: placeholders.name,
                    autoComplete: "name",
                    fullWidth: true,
                    onBlur: () => {
                        setUserDetailsFormData({
                            fieldName: field_names.name,
                        });
                    },
                    InputProps: {
                        className: checkoutCommonStyles.inputBaseText,
                    },
                    className: checkoutCommonStyles.inputCustomStyles,
                }}
            />
            <ExlyInput
                name={getUserFormFieldName(userId, field_names.email)}
                type="email"
                classes={{
                    helperText: checkoutCommonStyles.helperText,
                }}
                registerOptions={{
                    validate: emailValidator,
                }}
                overrideInputProps={{
                    placeholder: emailPlaceholder,
                    autoComplete: "email",
                    fullWidth: true,
                    onBlur: () => {
                        setUserDetailsFormData({
                            fieldName: field_names.email,
                        });
                    },
                    InputProps: {
                        className: checkoutCommonStyles.inputBaseText,
                    },
                    className: checkoutCommonStyles.inputCustomStyles,
                }}
                disabled={isUserEmailDisabled}
            />
            <div className={styles.PhoneInputWrapper}>
                <ExlyInput
                    name={getUserFormFieldName(userId, field_names.phone)}
                    type="tel"
                    classes={{
                        helperText: checkoutCommonStyles.helperText,
                    }}
                    registerOptions={{
                        validate: phoneNumberValidator,
                    }}
                    overrideInputProps={{
                        placeholder: placeholders.phone,
                        autoComplete: "phone",
                        fullWidth: true,
                        className: classNames(
                            styles.PhoneInput,
                            checkoutCommonStyles.inputCustomStyles
                        ),
                        InputProps: {
                            className: checkoutCommonStyles.inputBaseText,
                        },
                        onBlur: () => {
                            setUserDetailsFormData({
                                fieldName: field_names.phone,
                            });
                        },
                    }}
                />
                <CountryCodeAdornment
                    name={getUserFormFieldName(
                        userId,
                        field_names.country_code
                    )}
                    onChange={option => {
                        setUserDetailsFormData({
                            fieldName: field_names.country_code,
                            value: option.value,
                        });
                    }}
                />
            </div>
            {showGstBillingStateField ? (
                <div className={checkoutCommonStyles.dropdownBorderStyles}>
                    <ExlySearchableDropdownV2
                        name={getUserFormFieldName(
                            userId,
                            field_names.billing_state
                        )}
                        options={billingStatesOptions}
                        placeholder={
                            isBillingStateMandatory
                                ? placeholders.billing_state
                                : placeholders.billing_state_optional
                        }
                        classes={{
                            textInput: checkoutCommonStyles.inputBaseText,
                            helperText: checkoutCommonStyles.dropdownHelperText,
                            placeholder: checkoutCommonStyles.placeholderText,
                            inputRoot: checkoutCommonStyles.selectInput,
                            listbox: checkoutCommonStyles.selectMenuStyle,
                        }}
                        registerOptions={{
                            validate: isBillingStateMandatory
                                ? billingStateValidator
                                : undefined,
                        }}
                        onChange={option => {
                            setBillingStateFormData({
                                fieldName: field_names.billing_state,
                                value: option,
                            });
                        }}
                        ExpandIcon={<ExpandMore />}
                        disabled={isBillingStateDisabled}
                    />
                </div>
            ) : (
                <></>
            )}
            {/* 
                Note - The address details, part of user details are shown to international creator users 'or' international
                users 'or' if the main listing is of merchandise type
                We show delivery details to the user if its not an international creator or international
                user 'and' the main listing is not of Merchandise type 'and' an order bump of merchandise type is selected by the user
             */}
            {showAddressInsideUserDetails && (
                <InternationalDetailsFormGroup
                    showPaymentMethodInputField={showPaymentMethodInputField}
                    showInternationalInputFields={Boolean(
                        showInternationalInputFields
                    )}
                    currencyName={currencyName}
                />
            )}

            {/**
             * Pre Booking questions
             */}
            {!checkIsArrayEmpty(bookingQuestions) && (
                <div className={checkoutAccordianStyles.BasicForm}>
                    <CustomQuestionFormGroup
                        questions={bookingQuestions}
                        setFileUploaderFileValue={setFileUploaderFileValue}
                        clearFileUploaderFileValue={clearFileUploaderFileValue}
                        getInputFieldName={generateBookingQuestionFieldName}
                        setDetails={setUserDetailsFormData}
                        customQuestionHelperProps={
                            custom_questions_helper_props
                        }
                        generateUploadKey={generateUploadKey}
                        setIsCustomQuestionFileUploading={
                            setIsCustomQuestionFileUploading
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default UserDetailsFormBase;
